import React, { useEffect, useMemo, useRef, useState } from "react";
import ConfirmationModal from "ui-components/confirmation-modal/confirmationModal";
import "./dispatchLoadModal.scss";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import { useCarrierDropdown } from "hooks/useCarrier";
import { useDispatchLoad } from "hooks/useLoadboard";
import debounce from "lodash/debounce";
import EquipmentService from "app/data/equipment/equipmentServices";
import ManifestsService from "app/data/manifest/manifestsService";
import { DriverSearch } from "app/data/manifest/models";
import XGSIcon from "ui-components/icon/xgsIcon";
import XGSIcons from "ui-components/icon/xgsIcons";
import { toast } from "react-toastify";

export interface DispatchLoadModalProps {
    show: boolean;
    onClose: () => void;
    dispatchdata?: any;
}

const DispatchLoadModal: React.FC<DispatchLoadModalProps> = ({
    show,
    onClose,
    dispatchdata,
}) => {
    const equipmentService = EquipmentService.getInstance();
    const manifestService = ManifestsService.getInstance();
    const initializedRef = useRef(false);

    const [carrierFormValue, setCarrierFormValue] = useState<XGSSelectOption | null>(null);
    const [carrierSearch, setCarrierSearch] = useState<string>("");
    const [tractorFormValue, setTractorFormValue] = useState<XGSSelectOption | null>(null);
    const [trailerFormValue, setTrailerFormValue] = useState<XGSSelectOption | null>(null);
    const [driverFormValue, setDriverFormValue] = useState<XGSSelectOption | null>(null);

    const {
        data: carrierDropdownData = [],
        isLoading: isCarrierLoading,
    } = useCarrierDropdown();

    const carrierOptions = useMemo<XGSSelectOption[]>(() => {
        return carrierDropdownData
            .filter(c => c?.carrierNumber && c?.carrierName)
            .map(c => ({
                value: String(c.carrierNumber),
                label: (
                    <span>
                        {c.carrierName}{" "}
                        <span className="text-light">{c.carrierNumber}</span>
                    </span>
                ),
                sortLabel: `${c.carrierName} ${c.carrierNumber}`,
            }))
            .sort((a, b) => a.sortLabel!.localeCompare(b.sortLabel!));
    }, [carrierDropdownData]);

    const { mutate: dispatchLoad, isPending: isDispatching } = useDispatchLoad();

    const dispatchPayload = useMemo(() => ({
        manifestNumber: dispatchdata?.manifestNumber,
        doorNumber: dispatchdata?.doorNumber,
        carrierNumber: carrierFormValue?.value ?? null,
        tractorNumber: tractorFormValue?.value ?? null,
        trailerNumber: trailerFormValue?.value ?? null,
        driverId: driverFormValue?.value ?? null,
    }), [
        dispatchdata,
        carrierFormValue,
        tractorFormValue,
        trailerFormValue,
        driverFormValue,
    ]);

    const handleDispatch = () => {
        dispatchLoad(dispatchPayload, {
            onSuccess: () => {
                toast.success("Load dispatched successfully");
                onClose();
            },
            onError: (e) => {
                toast.error(`Failed to dispatch load. ${e || ""}`);
                onClose();
            }
        });
    };


    useEffect(() => {

        if (!show) {
            // Reset when modal is closed
            setCarrierFormValue(null);
            setTractorFormValue(null);
            setTrailerFormValue(null);
            setDriverFormValue(null);

            // Reset flag for next open
            initializedRef.current = false;
            return;
        }

        // Only populate once per open
        if (initializedRef.current) return;

        if (dispatchdata) {
            // Carrier
            if (dispatchdata.carrierNumber && carrierOptions.length) {
                const carrier = carrierOptions.find(
                    (o) => o.value === String(dispatchdata.carrierNumber)
                );
                setCarrierFormValue(carrier ?? null);
            }

            // Tractor
            if (dispatchdata.tractorNumber) {
                setTractorFormValue({
                    value: dispatchdata.tractorNumber,
                    label: dispatchdata.tractorNumber,
                });
            }

            // Trailer
            if (dispatchdata.trailerNumber) {
                setTrailerFormValue({
                    value: dispatchdata.trailerNumber,
                    label: dispatchdata.trailerNumber,
                });
            }

            // Driver
            if (dispatchdata.driver) {
                setDriverFormValue({
                    value: dispatchdata.driver,
                    label: dispatchdata.driver,
                });
            }

            // Mark as initialized for this open
            initializedRef.current = true;
        }
    }, [show, dispatchdata, carrierOptions]);

    //eslint-disable-next-line react-hooks/exhaustive-deps
    const loadTrailerOptions = React.useCallback(
        debounce((query: string, callback: (options: XGSSelectOption[]) => void) => {
            equipmentService.getEquipmentListQuery(query, 0)
                .then(response => {
                    const options = response.data?.content.map(e => ({
                        label: e.unitNumber || "",
                        value: e.unitNumber || ""
                    })) || [];
                    callback(options);
                })
                .catch(() => {
                    callback([]);
                });
        }, 300),
        []
    );

    //eslint-disable-next-line react-hooks/exhaustive-deps
    const loadDriverOptions = React.useCallback(
        debounce((query, callback) => {
            manifestService.getDriversSearch(query)
                .then(options => callback(options.map(({ driver, searchDetails }: DriverSearch) => ({
                    value: driver.driverId,
                    label: <span dangerouslySetInnerHTML={{ __html: searchDetails.matchedStr }} />
                }))))
        }, 300),
        []
    );

    const isFormValid =
        carrierFormValue !== null &&
        tractorFormValue !== null &&
        trailerFormValue !== null &&
        driverFormValue !== null;


    return (
        <ConfirmationModal
            opened={show}
            header={
                <div className="xgs-dispatch-load-modal__header-wrapper">
                    <div>
                        <div className="xgs-dispatch-load-modal__header">Dispatch Load?</div>
                        <p className="xgs-dispatch-load-modal__warning">
                            This will mark the load as departed. The truck will leave the dock.
                        </p>
                    </div>
                    <div className="xgs-dispatch-load-modal__close">
                        <XGSIcon
                            icon={XGSIcons.faTimes}
                            size="lg"
                            onClick={onClose}
                        />
                    </div>
                </div>
            }
            confirmButtonText="Dispatch Now"
            disableConfirmButton={!isFormValid || isDispatching}
            cancelButtonText="Cancel"
            onCancel={onClose}
            onConfirm={handleDispatch}
        >
            <div className="xgs-dispatch-load-modal">
                <div className="xgs-dispatch-load-modal__summary">
                    <h3 className="xgs-dispatch-load-modal__summary-title">Dispatch Summary</h3>

                    <div className="xgs-dispatch-load-modal__table">
                        <div className="xgs-dispatch-load-modal__row_data">
                            <span>Manifest:</span>
                            <span className="value">
                                {dispatchdata?.manifestNumber ?? "-"}
                            </span>
                        </div>
                        <div className="xgs-dispatch-load-modal__row_data">
                            <span>Door:</span>
                            <span className="value">{dispatchdata?.doorNumber ? `D-${dispatchdata.doorNumber}` : "-"}</span>
                        </div>
                        <div className="xgs-dispatch-load-modal__row_data">
                            <span>Destination:</span>
                            <span className="value">
                                {dispatchdata
                                    ? `${dispatchdata.destinationTerminalAirportCode} - ${dispatchdata.destinationTerminalName} ${dispatchdata.destinationTerminalNumber}`
                                    : "-"}</span>
                        </div>
                    </div>

                    <h3 className="xgs-dispatch-load-modal__summary-title">Dispatch Summary</h3>
                    <div className="xgs-dispatch-load-modal__fields">
                        <LabeledSelectInput
                            name="carrier"
                            label="Carrier"
                            labelMode={LabelModes.column}
                            placeholder="Select Carrier"
                            required
                            value={carrierFormValue}
                            options={carrierOptions}
                            isLoading={isCarrierLoading}
                            inputValue={carrierSearch}
                            onInputChange={setCarrierSearch}
                            onValueChange={onChangeCarrier => setCarrierFormValue(onChangeCarrier || null)}
                            className="xgs-dispatch-load-modal__field"
                        />

                        <LabeledSelectInput
                            name="tractor"
                            label="Tractor"
                            labelMode={LabelModes.column}
                            placeholder="Start Typing..."
                            required
                            value={tractorFormValue}
                            options={[]}
                            className="xgs-dispatch-load-modal__field"
                            onValueChange={onChangeTractor => setTractorFormValue(onChangeTractor || null)}
                            loadOptions={loadTrailerOptions}
                            async
                        />

                        <LabeledSelectInput
                            name="trailer"
                            label="Trailer"
                            labelMode={LabelModes.column}
                            placeholder="Start Typing..."
                            required
                            value={trailerFormValue}
                            onValueChange={onChangeTrailer => setTrailerFormValue(onChangeTrailer || null)}
                            loadOptions={loadTrailerOptions}
                            className="xgs-dispatch-load-modal__field"
                            options={[]}
                            async
                        />

                        <LabeledSelectInput
                            name="driver"
                            label="Driver"
                            labelMode={LabelModes.column}
                            placeholder="Search Driver"
                            required
                            value={driverFormValue}
                            onValueChange={onChangeDriver => setDriverFormValue(onChangeDriver || null)}
                            loadOptions={loadDriverOptions}
                            cacheOptions
                            async
                            options={[]}
                            className="xgs-dispatch-load-modal__field"
                        />
                    </div>
                </div>
            </div>
        </ConfirmationModal >
    );
};

export default DispatchLoadModal;